import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getActiveCartProductsAction, ActiveCartProductsInput } from '@msdyn365-commerce-modules/retail-actions';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getCartState } from '@msdyn365-commerce/global-state';

import { IProductQuantityPair, IProductWarranties } from './DataServiceEntities.g';
import { getProductWarrantiesAction, ProductWarrantiesInput } from './get-product-warranties.action';
import { pairGroupedItemLines } from '../utilities/product/utils';
import { IGroupedItemLine, ILineItem, ProductLineDataType } from '../utilities/utils';
import { getGroupedProductWarrantiesAsync } from './DataActionExtension.g';
import { BUILD_ID_COMMERCE_PROPERTY_KEY, IProductWarrantiesWithExtensionProperties } from '../utilities/warranty/utils';

/*
 * Input class for the getProductWarrantiesByCartInput data action
 */
export class GetProductWarrantiesByCartInput implements IActionInput {
    public getCacheKey = () => `mmx-productwarrantiesinactivecart`;
    public getCacheObjectType = () => 'IProductWarranties';
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Create input method for the getProductWarrantiesByCart data action.
 */
export const createProductWarrantiesByCartInput = (args: ICreateActionContext): IActionInput => {
    return new GetProductWarrantiesByCartInput();
};

/**
 * Action method for the getProductWarrntiesByCart data action.
 * @param input
 * @param ctx
 */
export async function getProductWarrantiesByCartAction(
    input: GetProductWarrantiesByCartInput,
    ctx: IActionContext
): Promise<IProductWarrantiesWithExtensionProperties[]> {
    var productWarrantiesInput: ProductWarrantiesInput[] = [];

    let groupedProductWarranties: IProductWarrantiesWithExtensionProperties[] = await _getBuildProductWarranties(ctx);

    let response: SimpleProduct[] = await getActiveCartProductsAction(new ActiveCartProductsInput(), ctx);

    response.forEach(cartProduct => {
        productWarrantiesInput.push(new ProductWarrantiesInput(cartProduct.RecordId));
    });

    const prodWarranties = (await getProductWarrantiesAction(productWarrantiesInput, ctx)).map(warranty => warranty);

    return [...prodWarranties, ...groupedProductWarranties];
}

async function _getBuildProductWarranties(ctx: IActionContext): Promise<IProductWarrantiesWithExtensionProperties[]> {
    let groupedProductWarranties: IProductWarrantiesWithExtensionProperties[] = [];
    let cartState = await getCartState(ctx);

    if (!cartState.cart?.CartLines) {
        return [];
    }

    let groupedItems = pairGroupedItemLines<ILineItem>(
        cartState.cart.CartLines.map(cl => {
            return { data: { cartline: cl } };
        }),
        ProductLineDataType.ICartlinesViewProps
    );

    let buildLines: IGroupedItemLine<ILineItem>[] = [];
    groupedItems.map(gi => {
        if (gi.buildItems && gi.buildItems.length > 0) {
            buildLines.push(gi);
        }
    });

    await Promise.all(
        buildLines.map(async bl => {
            if (!bl.item.data?.cartline.ProductId || !bl.item.data?.cartline.LineId) {
                return;
            }
            let buildLineProductQuantityPairs: IProductQuantityPair[] | undefined = bl.buildItems?.map(bi => {
                return { ProductId: bi.data?.cartline.ProductId, Quantity: bi.data?.cartline.Quantity };
            });
            if (!buildLineProductQuantityPairs || buildLineProductQuantityPairs.length === 0) {
                return;
            }
            return getGroupedProductWarrantiesAsync(
                { callerContext: ctx },
                buildLineProductQuantityPairs,
                bl.item.data!.cartline.ProductId
            ).then((response: IProductWarrantiesWithExtensionProperties[]) => {
                response.map(r => {
                    //if we don't deep copy the object, multiple builds will manipulate the same object and block duplicates from receiving warranties
                    const currentWarrantyGroup = { ...r };
                    currentWarrantyGroup.ExtensionProperties = [
                        { Key: BUILD_ID_COMMERCE_PROPERTY_KEY, Value: { StringValue: bl.item.data!.cartline.LineId } }
                    ];
                    groupedProductWarranties.push(currentWarrantyGroup);
                });
            });
        })
    );

    return groupedProductWarranties;
}

/**
 * The GetProductWarrantiesByCart data action
 * Gets all products in the active cart and finds the applicable
 * warranty for each product via the getProductWarranties RetailServer API
 */
export default createObservableDataAction({
    id: 'get-product-warranties-by-cart',
    action: <IAction<IProductWarranties[]>>getProductWarrantiesByCartAction,
    input: createProductWarrantiesByCartInput,
    isBatched: false
});
